import {
	Bike,
	FileText,
	Globe,
	Link2,
	Mail,
	MapPin,
	Megaphone,
	PackageCheck,
	QrCode,
	Share2,
	ShoppingCart,
	Sparkles,
	Store
} from 'lucide-react';

import { formatCurrency } from '@/lib/currency';

/**
 * Presentation only (NEA-5869). Weights and the scoring maths now live in the
 * backend (`weights.json` + `compute-score.js`); the resolver returns the
 * already-applied per-item uplifts and activation, so the client just maps an
 * item id → icon / copy / link / CTA. Item ids match the resolver's groups[]
 * (channels-data channel `type`s + the localActivations keys + quick-win ids).
 */

export const TARGET_SCORE = 60;

// The four self-attested wins have no platform signal — the retailer marks them
// done themselves (claimWin) rather than following a link.
export const SELF_ATTESTED_IDS = ['storefrontLinkOnSocials', 'emailButton', 'appleBusinessConnect', 'shopWindowQrCode'];

export const GROUP_META = {
	channels: {
		eyebrow: 'Channels',
		title: 'Show your products in more places',
		sub: 'Each channel you switch on puts your in-store stock in front of more local shoppers.'
	},
	tools: {
		eyebrow: 'Tools',
		title: 'Activate tools that turn views into visits',
		sub: 'Help shoppers go from discovery to your door — or to checkout.'
	},
	links: {
		eyebrow: 'Links',
		title: 'Link up your store',
		sub: "Send your existing audience to your Local Storefront, and back. This is the part Unify can't do for you."
	}
};

const PRESENTATION = {
	// Channels
	google: { icon: Store, title: 'Google Local Listings', body: 'Show your in-store stock on Google Search & Maps.' },
	storefront: { icon: Store, title: 'Local Storefront', body: 'Your public, searchable storefront for local shoppers.' },
	'meta-commerce': { icon: Share2, title: 'Meta Commerce', body: 'Sync your catalogue to Facebook & Instagram Shops.' },
	'tiktok-shop-feed': { icon: Share2, title: 'TikTok Product Feed', body: 'List your in-store products for TikTok shoppers.' },
	'pinterest-feed': { icon: Share2, title: 'Pinterest Feed', body: 'Shoppable Pins linked to your live local stock.' },
	'snap-store-feed': { icon: Share2, title: 'Snap Store Feed', body: 'Reach Snapchat shoppers near you.' },
	'uber-eats': { icon: Bike, title: 'Uber Eats', body: 'Sell your in-store products for on-demand delivery.' },
	deliveroo: { icon: Bike, title: 'Deliveroo', body: 'List your in-store products on Deliveroo.' },
	'just-eat': { icon: Bike, title: 'Just Eat', body: 'List your in-store products on Just Eat.' },
	// Tools
	'product-reservations': {
		icon: PackageCheck,
		title: 'Turn on Reservations',
		body: 'Let locals reserve a product before coming in to pay and pick up.'
	},
	'local-checkout': { icon: ShoppingCart, title: 'Activate Local Checkout', body: 'Let local shoppers buy online and pick up in store.' },
	'product-locator': { icon: MapPin, title: 'Product Locator', body: 'Embed an in-store availability finder on your own site.' },
	// Coming-soon Tools (NEA-5935) — presentation-only, non-scoring; surfaced as
	// disabled "Coming soon" cards in the Tools section (ported from fresh-dashboard).
	'click-collect': {
		icon: ShoppingCart,
		title: 'Click & Collect',
		body: 'Let shoppers fill their baskets and buy online to pick up in store.'
	},
	'product-page-linking': {
		icon: Link2,
		title: 'Product Page Linking',
		body: 'Link your Ads to your Local Storefront, so shoppers can go from search to buy in 2 taps.'
	},
	'local-ads': {
		icon: Megaphone,
		// The only priced title, so the only one that varies by shop — ad spend
		// is denominated in `shop.currency`.
		title: (currency) => `Switch on Local Inventory Ads (from ${formatCurrency(2, currency)}/day)`,
		body: 'Promote in-stock products to nearby shoppers searching on Google.'
	},
	// Links (storefront-dependent)
	storefrontProfileComplete: {
		icon: Globe,
		title: 'Add your website and social media links to your Storefront',
		body: 'Help shoppers find your other channels from your Storefront.',
		href: '/settings/store/storefront'
	},
	storefrontLinkOnSocials: {
		icon: Share2,
		title: 'Add your Local Storefront link to your social media',
		body: 'Put the link in the bio of your existing social accounts so followers can browse your live in-store stock.',
		// The Unified Commerce explainer, which opens on "Instagram & TikTok bio link" —
		// how to do this win. The old destination (storefront settings) was where you add
		// your socials TO your storefront, the opposite action (NEA-6023).
		href: '/learn/unified-commerce'
	},
	emailButton: {
		icon: Mail,
		title: '‘Search our products’ email button',
		body: 'Add a search-our-products button to your email footer.'
	},
	appleBusinessConnect: {
		icon: Link2,
		title: 'Apple Business Connect link',
		body: 'Add your storefront link to your Apple Business Connect profile.'
	},
	shopWindowQrCode: {
		icon: QrCode,
		title: 'Shop-window QR code',
		body: 'Put a printed QR code to your storefront in your shop window.'
	},
	// Quick-win-only (Overview surface)
	'make-products-visible': {
		icon: FileText,
		title: 'Make your in-stock products visible',
		body: 'Add a title so more of your products can show online.',
		href: '/inventory/manage?issue=missing-title'
	},
	'complete-profile': {
		icon: Store,
		title: 'Complete your store profile',
		body: 'Add your logo, contact email and your returns and delivery policies.',
		href: '/settings/store/storefront'
	}
};

const FALLBACK = { icon: Sparkles, title: 'Boost your Local Potential', body: '', href: '/channels' };

/**
 * Presentation for one Local Potential item. `currency` is only consulted by
 * the handful of entries whose copy quotes a price; everything else ignores it.
 */
export const presentationFor = (id, currency) => {
	const entry = PRESENTATION[id] ?? FALLBACK;
	return typeof entry.title === 'function' ? { ...entry, title: entry.title(currency) } : entry;
};

export const isSelfAttested = (id) => SELF_ATTESTED_IDS.includes(id);

// Where an item's CTA links, or null when the item has nowhere to go. Channels/
// tools always deep-link to their own channel subpage (NEA-5935) — the manage
// page once active, else the channel's detail page (which carries the right
// activation CTA per channel, e.g. Local Ads' "Set ad budget"). Links/quick-wins
// carry their own href; the catalogue is only the fallback for ids we don't
// recognise. A self-attested win is done outside the dashboard, so it only links
// somewhere if its preset points at a page that explains how (NEA-6023) —
// otherwise it links nowhere rather than somewhere misleading.
export const hrefFor = (item) => {
	const preset = presentationFor(item.id).href;
	if (preset) return preset;
	if (isSelfAttested(item.id)) return null;
	if (item.group === 'channels' || item.group === 'tools') return item.activated ? `/channels/${item.id}/manage` : `/channels/${item.id}`;
	return '/channels';
};
